import { useEffect, useState, useRef } from "react";
import client from "../lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, Sparkle, Plus, Trash, FilePdf, PencilSimple, Palette, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import LpgDeck from "./LpgDeck";

const clone = (o) => JSON.parse(JSON.stringify(o || {}));

const BROKERAGE_PRESETS = {
  "Custom / Independent": null,
  "Compass": { primary: "#1A1A1A", accent: "#B08D57" },
  "Keller Williams": { primary: "#B4131A", accent: "#8A8D8F" },
  "RE/MAX": { primary: "#C8102E", accent: "#003DA5" },
  "Coldwell Banker": { primary: "#012169", accent: "#B08D57" },
  "Berkshire Hathaway HS": { primary: "#552448", accent: "#B99A5B" },
  "eXp Realty": { primary: "#0C2340", accent: "#00A0DF" },
  "Sotheby's Int'l Realty": { primary: "#002349", accent: "#B7975B" },
  "Century 21": { primary: "#1A1A1A", accent: "#C9A96A" },
  "Redfin": { primary: "#A02021", accent: "#1A1A1A" },
};
const THEMES = [["editorial", "Editorial — brand on cream"], ["minimal", "Minimal — brand on white"], ["bold", "Bold — brand-forward"]];

export default function LpgPanel() {
  const [items, setItems] = useState([]);
  const [branding, setBranding] = useState(null);
  const [mode, setMode] = useState("list"); // list | review
  const [draft, setDraft] = useState(null);
  const [viewing, setViewing] = useState(null); // {data, branding, id}
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [eta, setEta] = useState(0);
  const progTimer = useRef(null);
  const [brandOpen, setBrandOpen] = useState(false);

  // address search
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [results, setResults] = useState([]);
  const [resolving, setResolving] = useState(false);
  const timer = useRef(null);
  const acSeq = useRef(0);

  const loadList = () => client.get("/lpg").then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { loadList(); client.get("/lpg/branding").then((r) => setBranding(r.data.branding)).catch(() => {}); }, []);

  const onType = (v) => {
    setQ(v); setSel(null);
    clearTimeout(timer.current);
    if (v.trim().length < 3) { setResults([]); return; }
    const mine = ++acSeq.current;
    timer.current = setTimeout(async () => {
      try { const r = await client.get("/booking/autocomplete", { params: { q: v } }); if (mine === acSeq.current) setResults(r.data.results || []); }
      catch { if (mine === acSeq.current) setResults([]); }
    }, 160);
  };

  const pick = async (r) => {
    setResults([]);
    setQ(r.label);
    setResolving(true);
    try {
      const { data } = await client.post("/booking/resolve-address",
        { street: r.street, city: r.city, state: r.state, zip: r.zip, lat: r.lat, lon: r.lon });
      setSel(data);
      setQ(data.label || r.label);
      if (!data.complete) toast.error("Couldn't confirm a full address (with ZIP) — please pick another suggestion.");
    } catch {
      setSel(null);
      toast.error("Couldn't verify that address. Please pick another from the list.");
    }
    setResolving(false);
  };

  const generate = async () => {
    if (!sel || !sel.complete) { toast.error("Please select a complete address (with ZIP) from the list."); return; }
    setBusy(true);
    setProgress(3); setEta(50);
    const start = Date.now();
    clearInterval(progTimer.current);
    progTimer.current = setInterval(() => {
      const elapsed = (Date.now() - start) / 1000;
      setProgress(Math.min(95, (elapsed / 50) * 100));
      setEta(Math.max(1, Math.ceil(50 - elapsed)));
    }, 400);
    try {
      const { data: job } = await client.post("/lpg/generate", { address: sel.street, city: sel.city, state: sel.state, zip: sel.zip, lat: sel.lat, lon: sel.lon });
      const draft = await pollJob(job.job_id);
      setProgress(100);
      setDraft(draft);
      setMode("review");
    } catch (e) { toast.error(e?.message || e?.response?.data?.detail || "Couldn't generate. Try again."); }
    clearInterval(progTimer.current);
    setBusy(false);
    setTimeout(() => setProgress(0), 700);
  };

  // Poll the background job — keeps every request short so Cloudflare never times out.
  const pollJob = (jobId) => new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = async () => {
      try {
        const { data } = await client.get(`/lpg/generate/status/${jobId}`);
        if (data.status === "done") return resolve(data.draft);
        if (data.status === "error") return reject(new Error(data.detail || "Couldn't generate. Try again."));
      } catch { /* transient network blip — keep polling until the overall timeout */ }
      if (Date.now() - started > 180000) return reject(new Error("Generation timed out. Please try again."));
      setTimeout(tick, 2500);
    };
    tick();
  });
  useEffect(() => () => clearInterval(progTimer.current), []);

  const set = (path, val) => {
    const d = clone(draft); let o = d;
    const keys = path.split(".");
    for (let i = 0; i < keys.length - 1; i++) { o[keys[i]] = o[keys[i]] ?? {}; o = o[keys[i]]; }
    o[keys[keys.length - 1]] = val;
    setDraft(d);
  };
  const setComp = (i, k, v) => { const d = clone(draft); d.comps[i][k] = v; setDraft(d); };
  const addComp = () => { const d = clone(draft); d.comps = d.comps || []; d.comps.push({ address: "", beds: "", baths: "", sqft: "", status: "SOLD", price: "", dom: "", sold_date: "", distance_note: "", narrative: "" }); setDraft(d); };
  const delComp = (i) => { const d = clone(draft); d.comps.splice(i, 1); setDraft(d); };

  // generic nested-array editors (schools.list, neighborhood.amenities)
  const _arr = (d, path) => {
    const keys = path.split("."); let o = d;
    for (let i = 0; i < keys.length - 1; i++) { o[keys[i]] = o[keys[i]] ?? {}; o = o[keys[i]]; }
    const last = keys[keys.length - 1]; o[last] = o[last] ?? []; return o[last];
  };
  const setArrItem = (path, i, k, v) => { const d = clone(draft); _arr(d, path)[i][k] = v; setDraft(d); };
  const addArrItem = (path, tmpl) => { const d = clone(draft); _arr(d, path).push({ ...tmpl }); setDraft(d); };
  const delArrItem = (path, i) => { const d = clone(draft); _arr(d, path).splice(i, 1); setDraft(d); };

  const save = async () => {
    setBusy(true);
    try {
      const r = await client.post("/lpg", { address: draft.address, data: draft, branding });
      toast.success("Presentation generated.");
      await loadList();
      setViewing({ data: draft, branding, id: r.data.id });
      setMode("list"); setDraft(null); setQ(""); setSel(null); setResults([]);
    } catch (e) { toast.error("Couldn't save."); }
    setBusy(false);
  };

  const openDeck = async (id) => {
    try { const r = await client.get(`/lpg/${id}`); setViewing({ data: r.data.data, branding: r.data.branding && Object.keys(r.data.branding).length ? r.data.branding : branding, id }); }
    catch { toast.error("Couldn't open that presentation."); }
  };
  const del = async (id) => { await client.delete(`/lpg/${id}`); loadList(); };

  const saveBranding = async () => { try { await client.put("/lpg/branding", branding); toast.success("Branding saved."); setBrandOpen(false); } catch { toast.error("Couldn't save branding."); } };
  const bset = (k, v) => setBranding((b) => ({ ...b, [k]: v }));
  const applyPreset = (name) => {
    const p = BROKERAGE_PRESETS[name];
    setBranding((b) => ({ ...b, brokerage: name === "Custom / Independent" ? b.brokerage : name, ...(p ? { primary: p.primary, accent: p.accent } : {}) }));
  };

  // ---- REVIEW EDITOR ----
  if (mode === "review" && draft) {
    const p = draft.property || {}, es = draft.executive_summary || {}, mk = draft.market || {}, rec = draft.recommendation || {}, nb = draft.neighborhood || {}, sch = draft.schools || {}, lf = draft.lifestyle || {};
    const F = ({ label, path, val, wide }) => (
      <div className={wide ? "col-span-2" : ""}>
        <Label className="text-xs">{label}</Label>
        <Input value={val ?? ""} onChange={(e) => set(path, e.target.value)} className="bg-white border-[#E9DDC6]" data-testid={`lpg-field-${path}`} />
      </div>
    );
    return (
      <div className="space-y-6 max-w-4xl" data-testid="lpg-review">
        <div>
          <p className="eyebrow text-xs text-[#B08D57] mb-1">Verify the research</p>
          <h2 className="font-display text-3xl text-[#4A0000]">{draft.address}</h2>
          {draft._source?.verified ? (
            <div className="mt-2 flex items-start gap-2 text-sm text-[#2E4F3B] bg-[#2E4F3B]/10 border border-[#2E4F3B]/25 rounded-[5px] p-3" data-testid="lpg-verified-banner">
              <CheckCircle size={18} weight="fill" className="shrink-0 mt-0.5" />
              <span>Beds, baths, sqft, lot & comps are <strong>verified from public property records</strong>{draft._source?.avm_price ? ` (est. value $${Number(draft._source.avm_price).toLocaleString()})` : ""}. Review the market stats & narrative below, then generate.</span>
            </div>
          ) : (
            <div className="mt-2 flex items-start gap-2 text-sm text-[#8A6D1B] bg-[#B08D57]/10 border border-[#B08D57]/25 rounded-[5px] p-3">
              <WarningCircle size={18} weight="fill" className="shrink-0 mt-0.5" />
              <span>These are <strong>AI-drafted estimates</strong>. Review and correct every figure below before generating — the deck uses exactly what you confirm here.</span>
            </div>
          )}
        </div>

        <div className="lux-card rounded-[5px] p-5">
          <h3 className="font-semibold mb-3">Property</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <F label="Beds" path="property.beds" val={p.beds} />
            <F label="Baths" path="property.baths" val={p.baths} />
            <F label="Sqft" path="property.sqft" val={p.sqft} />
            <F label="Year built" path="property.year_built" val={p.year_built} />
            <F label="Lot (acres)" path="property.lot_acres" val={p.lot_acres} />
            <F label="Garage" path="property.garage" val={p.garage} />
            <F label="HOA" path="property.hoa" val={p.hoa} />
            <F label="Subdivision" path="property.subdivision" val={p.subdivision} />
          </div>
          <div className="mt-3"><Label className="text-xs">Headline</Label><Input value={p.headline ?? ""} onChange={(e) => set("property.headline", e.target.value)} className="bg-white border-[#E9DDC6]" /></div>
        </div>

        <div className="lux-card rounded-[5px] p-5">
          <h3 className="font-semibold mb-3">Pricing & market</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <F label="Price — low (entry)" path="recommendation.price_low" val={rec.price_low} />
            <F label="Price — high (top)" path="recommendation.price_high" val={rec.price_high} />
            <F label="Likely lands near" path="recommendation.likely_price" val={rec.likely_price} />
            <F label="$/sqft" path="executive_summary.price_per_sqft" val={es.price_per_sqft} />
            <F label="Days to offer" path="executive_summary.days_to_offer" val={es.days_to_offer} />
            <F label="Range (display)" path="recommendation.value_range" val={rec.value_range} />
            <F label="Median sold" path="market.median_sold_price" val={mk.median_sold_price} />
            <F label="Median DOM" path="market.median_dom" val={mk.median_dom} />
          </div>
        </div>

        <div className="lux-card rounded-[5px] p-5">
          <div className="flex items-center justify-between mb-3"><h3 className="font-semibold">Comparable sales</h3>
            <Button size="sm" variant="outline" onClick={addComp} className="border-[#E9DDC6]" data-testid="lpg-add-comp"><Plus size={14} className="mr-1" />Add</Button></div>
          <div className="space-y-3">
            {(draft.comps || []).map((c, i) => (
              <div key={i} className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end border-b border-[#F0ECE2] pb-3" data-testid={`lpg-comp-${i}`}>
                <div className="col-span-2"><Label className="text-[10px]">Address</Label><Input value={c.address} onChange={(e) => setComp(i, "address", e.target.value)} className="bg-white border-[#E9DDC6] h-9" /></div>
                <div><Label className="text-[10px]">Price</Label><Input value={c.price} onChange={(e) => setComp(i, "price", e.target.value)} className="bg-white border-[#E9DDC6] h-9" /></div>
                <div><Label className="text-[10px]">Sqft</Label><Input value={c.sqft} onChange={(e) => setComp(i, "sqft", e.target.value)} className="bg-white border-[#E9DDC6] h-9" /></div>
                <div><Label className="text-[10px]">Bd/Ba</Label><Input value={`${c.beds}/${c.baths}`} onChange={(e) => { const [bd, ba] = e.target.value.split("/"); setComp(i, "beds", bd); setComp(i, "baths", ba || ""); }} className="bg-white border-[#E9DDC6] h-9" /></div>
                <button onClick={() => delComp(i)} className="text-red-500 hover:text-red-700 justify-self-end pb-1.5" data-testid={`lpg-del-comp-${i}`}><Trash size={16} /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="lux-card rounded-[5px] p-5">
          <h3 className="font-semibold mb-3">Narrative</h3>
          <Label className="text-xs">Neighborhood summary</Label>
          <Textarea value={nb.summary ?? ""} onChange={(e) => set("neighborhood.summary", e.target.value)} className="bg-white border-[#E9DDC6] mb-3" />
          <Label className="text-xs">Recommendation summary</Label>
          <Textarea value={rec.summary ?? ""} onChange={(e) => set("recommendation.summary", e.target.value)} className="bg-white border-[#E9DDC6]" />
        </div>

        <div className="lux-card rounded-[5px] p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Neighborhood amenities</h3>
            <Button size="sm" variant="outline" onClick={() => addArrItem("neighborhood.amenities", { name: "", desc: "", distance: "" })} className="border-[#E9DDC6]" data-testid="lpg-add-amenity"><Plus size={14} className="mr-1" />Add</Button>
          </div>
          <div className="mb-3"><Label className="text-xs">Neighborhood name</Label><Input value={nb.name ?? ""} onChange={(e) => set("neighborhood.name", e.target.value)} className="bg-white border-[#E9DDC6]" /></div>
          <div className="space-y-2">
            {(nb.amenities || []).map((a, i) => (
              <div key={i} className="grid grid-cols-2 sm:grid-cols-7 gap-2 items-end border-b border-[#F0ECE2] pb-2" data-testid={`lpg-amenity-${i}`}>
                <div className="sm:col-span-2"><Label className="text-[10px]">Name</Label><Input value={a.name} onChange={(e) => setArrItem("neighborhood.amenities", i, "name", e.target.value)} className="bg-white border-[#E9DDC6] h-9" /></div>
                <div className="sm:col-span-3"><Label className="text-[10px]">Description</Label><Input value={a.desc} onChange={(e) => setArrItem("neighborhood.amenities", i, "desc", e.target.value)} className="bg-white border-[#E9DDC6] h-9" /></div>
                <div><Label className="text-[10px]">Distance</Label><Input value={a.distance} onChange={(e) => setArrItem("neighborhood.amenities", i, "distance", e.target.value)} className="bg-white border-[#E9DDC6] h-9" /></div>
                <button onClick={() => delArrItem("neighborhood.amenities", i)} className="text-red-500 hover:text-red-700 justify-self-end pb-1.5" data-testid={`lpg-del-amenity-${i}`}><Trash size={16} /></button>
              </div>
            ))}
            {(nb.amenities || []).length === 0 && <p className="text-sm text-[#5C5A58]">No amenities yet — add nearby parks, dining, retail or landmarks.</p>}
          </div>
          <div className="mt-3"><Label className="text-xs">Tailwinds</Label><Input value={nb.tailwinds ?? ""} onChange={(e) => set("neighborhood.tailwinds", e.target.value)} className="bg-white border-[#E9DDC6]" /></div>
        </div>

        <div className="lux-card rounded-[5px] p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Schools</h3>
            <Button size="sm" variant="outline" onClick={() => addArrItem("schools.list", { name: "", grades: "", distance: "", rating: "", score: "", desc: "" })} className="border-[#E9DDC6]" data-testid="lpg-add-school"><Plus size={14} className="mr-1" />Add</Button>
          </div>
          <div className="mb-3"><Label className="text-xs">District</Label><Input value={sch.district ?? ""} onChange={(e) => set("schools.district", e.target.value)} className="bg-white border-[#E9DDC6]" /></div>
          <div className="space-y-2">
            {(sch.list || []).map((s, i) => (
              <div key={i} className="grid grid-cols-2 sm:grid-cols-7 gap-2 items-end border-b border-[#F0ECE2] pb-2" data-testid={`lpg-school-${i}`}>
                <div className="sm:col-span-2"><Label className="text-[10px]">Name</Label><Input value={s.name} onChange={(e) => setArrItem("schools.list", i, "name", e.target.value)} className="bg-white border-[#E9DDC6] h-9" /></div>
                <div><Label className="text-[10px]">Grades</Label><Input value={s.grades} onChange={(e) => setArrItem("schools.list", i, "grades", e.target.value)} className="bg-white border-[#E9DDC6] h-9" /></div>
                <div><Label className="text-[10px]">Distance</Label><Input value={s.distance} onChange={(e) => setArrItem("schools.list", i, "distance", e.target.value)} className="bg-white border-[#E9DDC6] h-9" /></div>
                <div><Label className="text-[10px]">Score</Label><Input value={s.score} onChange={(e) => setArrItem("schools.list", i, "score", e.target.value)} className="bg-white border-[#E9DDC6] h-9" placeholder="8/10" /></div>
                <div><Label className="text-[10px]">Rating</Label><Input value={s.rating} onChange={(e) => setArrItem("schools.list", i, "rating", e.target.value)} className="bg-white border-[#E9DDC6] h-9" /></div>
                <button onClick={() => delArrItem("schools.list", i)} className="text-red-500 hover:text-red-700 justify-self-end pb-1.5" data-testid={`lpg-del-school-${i}`}><Trash size={16} /></button>
              </div>
            ))}
            {(sch.list || []).length === 0 && <p className="text-sm text-[#5C5A58]">No schools yet — add the assigned elementary, middle and high schools.</p>}
          </div>
          <div className="mt-3"><Label className="text-xs">Disclaimer</Label><Input value={sch.disclaimer ?? ""} onChange={(e) => set("schools.disclaimer", e.target.value)} className="bg-white border-[#E9DDC6]" placeholder="Verify attendance zones before relying on assignments." /></div>
        </div>

        <div className="lux-card rounded-[5px] p-5">
          <h3 className="font-semibold mb-3">Lifestyle scores</h3>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">Walk score</Label><Input value={lf.walk_score ?? ""} onChange={(e) => set("lifestyle.walk_score", e.target.value)} className="bg-white border-[#E9DDC6]" data-testid="lpg-field-lifestyle.walk_score" /></div>
            <div><Label className="text-xs">Bike score</Label><Input value={lf.bike_score ?? ""} onChange={(e) => set("lifestyle.bike_score", e.target.value)} className="bg-white border-[#E9DDC6]" /></div>
            <div><Label className="text-xs">Transit score</Label><Input value={lf.transit_score ?? ""} onChange={(e) => set("lifestyle.transit_score", e.target.value)} className="bg-white border-[#E9DDC6]" /></div>
          </div>
        </div>

        <div className="flex gap-3 sticky bottom-0 bg-[#FBFAF7]/90 backdrop-blur-md py-3 -mx-1 px-1">
          <Button onClick={save} disabled={busy} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white" data-testid="lpg-generate-deck"><Sparkle size={16} weight="fill" className="mr-2" />{busy ? "Generating…" : "Generate branded deck"}</Button>
          <Button variant="outline" onClick={() => { setMode("list"); setDraft(null); }} className="border-[#E9DDC6]">Cancel</Button>
        </div>
      </div>
    );
  }

  // ---- LIST + NEW ----
  return (
    <div className="space-y-6 max-w-4xl" data-testid="lpg-panel">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex-1 min-w-0 w-full">
          <p className="eyebrow text-xs text-[#B08D57] mb-1">Marketing</p>
          <h2 className="font-display text-3xl text-[#4A0000]">Listing Presentations</h2>
          <p className="text-sm text-[#5C5A58] mt-1 w-full md:whitespace-nowrap">Type an address, verify the research, and generate a branded, client-ready deck — free and unlimited.</p>
        </div>
        <Button variant="outline" onClick={() => setBrandOpen(true)} className="border-[#E9DDC6] shrink-0 hover:bg-[#B08D57]/12 hover:text-[#4A0000] hover:border-[#B08D57]" data-testid="lpg-branding-btn"><Palette size={16} className="mr-2" />Branding</Button>
      </div>

      <div className="relative overflow-hidden rounded-xl p-6 sm:p-7 shadow-premium-lg ring-1 ring-[#B08D57]/40" style={{ background: "linear-gradient(135deg, #4A0000 0%, #5C1A1A 55%, #2A0A0A 100%)" }} data-testid="lpg-new-card">
        <div className="pointer-events-none absolute -top-20 -right-16 w-60 h-60 rounded-full bg-[#B08D57]/25 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#B08D57]/20 ring-1 ring-[#B08D57]/50 shrink-0"><Sparkle size={20} weight="fill" className="text-[#E4C97E]" /></span>
            <div className="min-w-0">
              <h3 className="font-display text-lg sm:text-2xl text-white leading-none truncate">Start a new presentation</h3>
              <p className="text-xs sm:text-sm text-white/65 mt-1.5">Type an address to generate a branded, client-ready deck in seconds.</p>
            </div>
          </div>
          <div className="flex gap-2 mt-5">
            <div className="relative flex-1">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A968E]" />
              <Input value={q} onChange={(e) => onType(e.target.value)} placeholder="Begin typing a property address…" className="bg-white border-transparent pl-9 h-11 shadow-sm focus-visible:ring-2 focus-visible:ring-[#E4C97E] placeholder:text-xs sm:placeholder:text-sm" data-testid="lpg-address-input" />
              {results.length > 0 && !sel && (
                <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-[#E9DDC6] rounded-[5px] shadow-premium-lg max-h-64 overflow-y-auto" data-testid="lpg-address-results">
                  {results.map((r, i) => (
                    <button key={i} onClick={() => pick(r)} className="block w-full text-left px-3 py-2 text-sm hover:bg-[#FAF9F6]" data-testid={`lpg-address-opt-${i}`}>{r.label}</button>
                  ))}
                </div>
              )}
            </div>
            <Button onClick={generate} disabled={busy || resolving || !sel?.complete} className="h-11 px-5 bg-gradient-to-r from-[#C6A253] to-[#B08D57] text-[#1A1715] hover:from-[#D0AC5C] hover:to-[#B99A5B] font-semibold shrink-0 shadow-md disabled:opacity-60" data-testid="lpg-generate-btn"><Sparkle size={16} weight="fill" className="mr-2" />{resolving ? "Verifying…" : busy ? "Researching…" : "Generate"}</Button>
          </div>
          {busy && (
            <div className="mt-4" data-testid="lpg-progress">
              <div className="flex items-center justify-between text-xs text-[#E4C97E] mb-1.5">
                <span className="flex items-center gap-1.5"><Sparkle size={12} weight="fill" className="animate-pulse" />
                  {progress < 25 ? "Pulling property records…" : progress < 55 ? "Analyzing comparable sales…" : progress < 85 ? "Assessing market & pricing…" : "Composing your presentation…"}
                </span>
                <span className="tabular-nums text-white/70">~{eta}s left</span>
              </div>
              <div className="h-2 rounded-full bg-white/15 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-[#E4C97E] to-[#B08D57] transition-[width] duration-500 ease-out" style={{ width: `${progress}%` }} data-testid="lpg-progress-bar" />
              </div>
            </div>
          )}
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-[#1A1715] mb-3">Your presentations</h3>
        {items.length === 0 ? (
          <div className="text-center py-16 text-[#5C5A58] border border-dashed border-[#E9DDC6] rounded-md" data-testid="lpg-empty">
            <FilePdf size={40} weight="duotone" className="mx-auto mb-3 text-[#4A0000]" />
            <p>No presentations yet. Type an address above to create your first.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {items.map((it) => (
              <div key={it.id} className="lux-card rounded-[5px] p-4 flex items-center justify-between gap-3 hover:shadow-premium transition-shadow" data-testid={`lpg-item-${it.id}`}>
                <button onClick={() => openDeck(it.id)} className="text-left min-w-0 flex-1">
                  <p className="font-semibold text-[#1A1715] truncate">{it.address}</p>
                  <p className="text-xs text-[#5C5A58]">{it.owner_name ? `${it.owner_name} · ` : ""}{it.created_at ? new Date(it.created_at).toLocaleDateString() : ""}</p>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => openDeck(it.id)} className="p-2 rounded-md hover:bg-[#FAF9F6] text-[#4A0000]" data-testid={`lpg-open-${it.id}`}><FilePdf size={16} /></button>
                  <button onClick={() => del(it.id)} className="p-2 rounded-md hover:bg-red-50 text-red-500" data-testid={`lpg-delete-${it.id}`}><Trash size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {viewing && <LpgDeck data={viewing.data} branding={viewing.branding} onClose={() => setViewing(null)} onEdit={() => { setDraft(viewing.data); setViewing(null); setMode("review"); }} />}

      <Dialog open={brandOpen} onOpenChange={setBrandOpen}>
        <DialogContent className="max-w-lg bg-[#FBFAF7] max-h-[88vh] overflow-y-auto" data-testid="lpg-branding-dialog">
          <DialogHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 pr-6 sm:pr-9">
              <DialogTitle className="text-left font-display text-2xl text-[#4A0000]">Your branding</DialogTitle>
              {branding && (() => {
                const primary = branding.primary || "#4A0000";
                const surf = { editorial: primary, minimal: `color-mix(in srgb, ${primary} 20%, #14110F)`, bold: primary }[branding.theme || "editorial"];
                const light = (branding.theme || "editorial") === "minimal" ? "#FFFFFF" : "#FBFAF7";
                return (
                  <div className="flex items-center gap-1.5" data-testid="brand-theme-preview" title="Deck colors follow your brokerage brand">
                    <span className="text-[10px] text-[#9A968E] uppercase tracking-wide mr-1">Preview</span>
                    <span className="w-5 h-5 rounded-[3px] border border-black/10" style={{ background: surf }} />
                    <span className="w-5 h-5 rounded-[3px] border border-black/10" style={{ background: light }} />
                    <span className="w-5 h-5 rounded-[3px] border border-black/10" style={{ background: branding.accent || "#B08D57" }} />
                  </div>
                );
              })()}
            </div>
          </DialogHeader>
          {branding && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Primary color</Label><div className="flex gap-2"><input type="color" value={branding.primary || "#4A0000"} onChange={(e) => bset("primary", e.target.value)} className="h-10 w-12 rounded border border-[#E9DDC6]" data-testid="brand-primary" /><Input value={branding.primary || ""} onChange={(e) => bset("primary", e.target.value)} className="bg-white border-[#E9DDC6]" /></div></div>
                <div><Label className="text-xs">Accent color</Label><div className="flex gap-2"><input type="color" value={branding.accent || "#B08D57"} onChange={(e) => bset("accent", e.target.value)} className="h-10 w-12 rounded border border-[#E9DDC6]" /><Input value={branding.accent || ""} onChange={(e) => bset("accent", e.target.value)} className="bg-white border-[#E9DDC6]" /></div></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Brokerage preset</Label>
                  <select data-testid="brand-preset" defaultValue="" onChange={(e) => applyPreset(e.target.value)} className="w-full rounded-[5px] border border-[#E9DDC6] bg-white px-2 py-2 text-sm">
                    <option value="" disabled>Auto-fill colors…</option>
                    {Object.keys(BROKERAGE_PRESETS).map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Deck theme</Label>
                  <select data-testid="brand-theme" value={branding.theme || "editorial"} onChange={(e) => bset("theme", e.target.value)} className="w-full rounded-[5px] border border-[#E9DDC6] bg-white px-2 py-2 text-sm">
                    {THEMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Agent name</Label><Input value={branding.agent_name || ""} onChange={(e) => bset("agent_name", e.target.value)} className="bg-white border-[#E9DDC6]" data-testid="brand-name" /></div>
                <div><Label className="text-xs">Title</Label><Input value={branding.title || ""} onChange={(e) => bset("title", e.target.value)} className="bg-white border-[#E9DDC6]" /></div>
                <div><Label className="text-xs">Brokerage</Label><Input value={branding.brokerage || ""} onChange={(e) => bset("brokerage", e.target.value)} className="bg-white border-[#E9DDC6]" data-testid="brand-brokerage" /></div>
                <div><Label className="text-xs">Phone</Label><Input value={branding.phone || ""} onChange={(e) => bset("phone", e.target.value)} className="bg-white border-[#E9DDC6]" /></div>
                <div><Label className="text-xs">Email</Label><Input value={branding.email || ""} onChange={(e) => bset("email", e.target.value)} className="bg-white border-[#E9DDC6]" /></div>
                <div><Label className="text-xs">Website</Label><Input value={branding.website || ""} onChange={(e) => bset("website", e.target.value)} className="bg-white border-[#E9DDC6]" /></div>
              </div>
              <div><Label className="text-xs">Profile photo URL</Label><Input value={branding.avatar || ""} onChange={(e) => bset("avatar", e.target.value)} className="bg-white border-[#E9DDC6]" placeholder="https://…" /></div>
              <div><Label className="text-xs">Brokerage logo URL</Label><Input value={branding.logo || ""} onChange={(e) => bset("logo", e.target.value)} className="bg-white border-[#E9DDC6]" placeholder="https://…" /></div>
              <Button onClick={saveBranding} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white w-full" data-testid="brand-save">Save branding</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
